import { zodResolver } from '@hookform/resolvers/zod'
import { useContext } from 'react'
import { FieldValues, DefaultValues, useForm, SubmitHandler } from 'react-hook-form'
import { ZodSchema } from 'zod'
import { CreateCampaignContext, STEP } from '~/contexts/CreateCampaignContext'

type Props<Input extends FieldValues, Output extends FieldValues> = {
  defaultValues: DefaultValues<Input>
  commit?: (data: Output) => void
  schema?: ZodSchema
}

/**
 * キャンペーン作成のフォームで使用されるカスタムフックです。
 * フォームの送信やページの移動を管理します。
 *
 * @param defaultValues - フォームの初期値
 * @param commit - フォームデータをcontextに保存するためのコールバック関数
 * @returns onSubmit - フォーム送信時のハンドラー
 * @returns control - フォームのコントロールオブジェクト
 * @returns getValues - フォームの値を取得する関数
 * @returns setValue - フォームの値を設定する関数
 * @returns setError - フォームにエラーを設定する関数
 * @returns onBack - 戻るボタンが押されたときのハンドラー
 * @returns useFormMethods - react-hook-formのメソッドを含むオブジェクト
 */

export const useCampaignPageStepForm = <Input extends FieldValues, Output extends FieldValues = Input>({
  defaultValues,
  commit: commitParam,
  schema,
}: Props<Input, Output>) => {
  const { changePageStep, setCampaign } = useContext(CreateCampaignContext)
  const useFormMethods = useForm<Input, unknown, Output>({
    defaultValues,
    resolver: schema ? zodResolver(schema) : undefined,
    mode: 'onBlur',
  })
  const commit =
    commitParam ??
    ((data: Output) => {
      setCampaign((campaign) => {
        return {
          ...campaign,
          ...data,
        }
      })
    })
  const { handleSubmit, control, getValues, setValue, setError } = useFormMethods
  const handler = (data: Output) => {
    commit(data)
    changePageStep(STEP.NEXT)
  }
  const onSubmit = handleSubmit(
    handler as Output extends undefined
      ? SubmitHandler<Input>
      : Output extends FieldValues
        ? SubmitHandler<Output>
        : never,
  )
  const onBack = () => {
    setCampaign((prev) => ({
      ...prev,
      ...getValues(),
    }))
    changePageStep(STEP.PREV)
  }

  return { onSubmit, control, getValues, setValue, setError, onBack, useFormMethods }
}
